import { Navigate, Route, Routes, useLocation } from 'react-router';
import { sessionEndedByExpiry, useSession } from './lib/auth.ts';
import { loginUrl } from './lib/safeReturn.ts';
import { Layout } from './components/Layout.tsx';
import { LoginPage } from './pages/Login.tsx';
import { LoginLinkPage } from './pages/LoginLink.tsx';
import { ForgotPage } from './pages/Forgot.tsx';
import { ResetPage } from './pages/Reset.tsx';
import { ArtifactsPage } from './pages/Artifacts.tsx';
import { ArtifactPage } from './pages/Artifact.tsx';
import { RunPage } from './pages/Run.tsx';
import { AiPage } from './pages/Ai.tsx';
import { UsersPage } from './pages/Users.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { MailConnectedPage } from './pages/MailConnected.tsx';
import { AppCallsProvider } from './components/AppCalls.tsx';
import { Disclaimer } from './components/Disclaimer.tsx';
import { MfaGate } from './components/Mfa.tsx';
import { SecurityPage } from './pages/Security.tsx';
import { KnowledgePage } from './pages/Knowledge.tsx';

function RequireSession({ children }: { children: React.ReactNode }) {
  const s = useSession();
  const loc = useLocation();
  if (!s) return <Navigate to={loginUrl(loc.pathname + loc.search, sessionEndedByExpiry() ? 'session_expired' : undefined)} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* 26: קישור כניסה חד-פעמי — token ב-fragment, מחוץ ל-RequireSession (מחליף כניסה קיימת) */}
      <Route path="/login/link" element={<LoginLinkPage />} />
      {/* platform-password-recovery: "שכחתי סיסמה" (GoTrue /recover) וקביעת סיסמה חדשה מהמייל — token ב-fragment, מחליף כניסה קיימת */}
      <Route path="/login/forgot" element={<ForgotPage />} />
      <Route path="/login/reset" element={<ResetPage />} />
      {/* 20: לאן גוגל מחזירה — החלון הקופץ מודיע ל-drawer "חיבור Gmail" ונסגר (בלי session, בלי מעטפת) */}
      <Route path="/mail/connected" element={<MailConnectedPage />} />
      <Route
        element={
          <RequireSession>
            {/* platform-mfa-totp: קוד מאפליקציית האימות / הצטרפות — לפני המעטפת, לכל כניסה (סיסמה וקישור חד-פעמי) */}
            <MfaGate>
              {/* 16: ab.call של בלוקי app — הגשר עם ה-JWT של המשתמש */}
              <AppCallsProvider>
                <Layout />
              </AppCallsProvider>
            </MfaGate>
          </RequireSession>
        }
      >
        <Route index element={<Navigate to="/artifacts" replace />} />
        <Route path="/artifacts" element={<ArtifactsPage />} />
        <Route path="/artifacts/:slug" element={<ArtifactPage />} />
        <Route path="/artifacts/:slug/runs/:runId" element={<RunPage />} />
        {/* 20: אין דף קבוע לחיבור Gmail — קישור ישן/עמוק פותח את ה-drawer "חיבור Gmail" */}
        <Route path="/settings/mail" element={<Navigate to="/artifacts?mail=manage" replace />} />
        {/* platform-mfa-totp: אפליקציית אימות — הפעלה / החלפת מכשיר (מתפריט המשתמש) */}
        <Route path="/settings/security" element={<SecurityPage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        {/* /ai1, /ai2 נגישים בכתובת לכל אחד — השרת עונה 403 והמסך מציג אותו (11 §5.2) */}
        {/* key לפי רמה: מעבר מנהל בינה ↔ אייבי הוא מסך חדש (state, SSE, טיוטה, קבצים, פאנל — לא עוברים בין הרמות) */}
        <Route path="/ai2/:conversationId?" element={<AiPage key="ai2" level={2} />} />
        <Route path="/ai1/:conversationId?" element={<AiPage key="ai1" level={1} />} />
        {/* users-delegation: admin ובעל הרשאת האצלה; אחר רואה את הודעת השרת (403) */}
        <Route path="/users" element={<UsersPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    {/* web-ai-disclaimer: בכל מסך, גם בכניסה */}
    <Disclaimer />
    </>
  );
}
